// EPSE Time Context Processing
import { TimeContext, OperatingSchedule, NL_HOLIDAYS_2024, NL_HOLIDAYS_2025 } from '@/types/epse';

const ALL_NL_HOLIDAYS = [...NL_HOLIDAYS_2024, ...NL_HOLIDAYS_2025];

/**
 * Label a timestamp with complete time context
 */
export function labelTimeContext(
  timestamp: Date | string,
  schedule?: OperatingSchedule
): TimeContext {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  
  const hour = date.getHours();
  const minute = date.getMinutes();
  const quarter = Math.floor(minute / 15);
  const quarterOfDay = hour * 4 + quarter;
  
  const dayOfWeek = date.getDay(); // 0 = Sunday
  const month = date.getMonth() + 1;
  const dateStr = date.toISOString().split('T')[0];
  
  // Determine day type
  const isHoliday = ALL_NL_HOLIDAYS.includes(dateStr);
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  
  let dayType: 'werkdag' | 'weekend' | 'feestdag' = 'werkdag';
  if (isHoliday) {
    dayType = 'feestdag';
  } else if (isWeekend) {
    dayType = 'weekend';
  }
  
  // Determine season (meteorological)
  let season: 'winter' | 'lente' | 'zomer' | 'herfst';
  if (month >= 3 && month <= 5) {
    season = 'lente';
  } else if (month >= 6 && month <= 8) {
    season = 'zomer';
  } else if (month >= 9 && month <= 11) {
    season = 'herfst';
  } else {
    season = 'winter';
  }
  
  // Night definition: 22:00 - 06:00
  const isNight = hour >= 22 || hour < 6;
  
  // Business hours check
  let withinBusinessHours = false;
  if (schedule?.business_hours && dayType === 'werkdag') {
    const [startHour, startMin] = schedule.business_hours.start.split(':').map(Number);
    const [endHour, endMin] = schedule.business_hours.end.split(':').map(Number);
    
    const currentMinutes = hour * 60 + minute;
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    withinBusinessHours = currentMinutes >= startMinutes && currentMinutes < endMinutes;
  }
  
  // Shift detection
  let shiftId: string | null = null;
  if (schedule?.shifts) {
    for (const shift of schedule.shifts) {
      if (!shift.days.includes(dayOfWeek)) continue;
      
      const [startHour, startMin] = shift.start_time.split(':').map(Number);
      const [endHour, endMin] = shift.end_time.split(':').map(Number);
      
      const currentMinutes = hour * 60 + minute;
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      
      // Handle overnight shifts
      if (endMinutes < startMinutes) {
        if (currentMinutes >= startMinutes || currentMinutes < endMinutes) {
          shiftId = shift.id;
          break;
        }
      } else {
        if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
          shiftId = shift.id;
          break;
        }
      }
    }
  }
  
  // Week of year calculation
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
  const weekOfYear = Math.ceil((days + startOfYear.getDay() + 1) / 7);
  
  return {
    hour_of_day: hour,
    quarter_of_day: quarterOfDay,
    is_night: isNight,
    day_type: dayType,
    season,
    within_business_hours: withinBusinessHours,
    shift_id: shiftId,
    month,
    week_of_year: weekOfYear,
  };
}

/**
 * Get season label in Dutch
 */
export function getSeasonLabel(season: string): string {
  const labels: Record<string, string> = {
    winter: 'Winter',
    lente: 'Lente',
    zomer: 'Zomer',
    herfst: 'Herfst',
  };
  return labels[season] || season;
}

/**
 * Get day type label in Dutch
 */
export function getDayTypeLabel(dayType: string): string {
  const labels: Record<string, string> = {
    werkdag: 'Werkdag',
    weekend: 'Weekend',
    feestdag: 'Feestdag',
  };
  return labels[dayType] || dayType;
}

/**
 * Format quarter index (0-95) to time string
 */
export function quarterToTimeString(quarterIndex: number): string {
  const hour = Math.floor(quarterIndex / 4);
  const minute = (quarterIndex % 4) * 15;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

/**
 * Get all quarters for a day
 */
export function getAllQuarterTimes(): string[] {
  return Array.from({ length: 96 }, (_, i) => quarterToTimeString(i));
}
